import { createAuthClient } from "better-auth/client";
import "./admin-readiness-ui.js";
import { authErrorMessage } from "./auth-error-message.js";
import { twoFactorClient } from "better-auth/client/plugins";
import QRCode from "qrcode";
import { applySyncEvent, createWebHistoryAccumulator, disposeWebHistoryAccumulator, finishWebHistory, parseRecoveryKey, safeWebImageUrl } from "./history-crypto.js";
import { clearHistoryUnlockSession, defaultHistoryUnlockMinutes, formatHistoryUnlockRemaining, readHistoryUnlockSession, saveHistoryUnlockSession } from "./history-session.js";
import { detectDownloadPlatform, visibleDownloadPlatforms } from "./download-platform.js";
import { macDownloadNotice, macInstallGuide } from "./mac-download-info.js";
import { landingMarkup, landingNavLinks } from "./landing-page.js";
import { loadMediaManifest } from "./landing-media.js";
import { startLandingMotion } from "./landing-motion.js";
import "@fontsource-variable/bricolage-grotesque/opsz.css";
import "@fontsource/jetbrains-mono/latin-500.css";
import "@fontsource/jetbrains-mono/latin-700.css";
import "./auth-error.css";
import "./web.css";
import "./brand.css";
import "./landing.css";
// release-version.py keeps this package version equal to the desktop release.
import { version as releaseVersion } from "../package.json";

const authClient = createAuthClient({
  baseURL: window.location.origin,
  plugins: [twoFactorClient({ twoFactorPage: "/two-factor" })],
});

const app = document.querySelector("#app");
let account = null;
let enrollment = null;
let devices = [];
let webHistory = { unlocked: false, loading: false, clips: [], objectUrls: [], error: "", expiresAt: 0 };
let historyCountdownTimer = null;
let historyAbortController = null;
// Bumped at sign-out so account loads and History unlocks started earlier
// cannot render or restore decrypted state into the signed-out tab.
let sessionGeneration = 0;
let adminInvites = [];
let adminFeedback = [];
let adminFeedbackFilter = "all";
let adminFeedbackRequest = 0;
let adminFeedbackCursors = [null];
let adminFeedbackNextCursor = null;
const visitorPlatform = detectDownloadPlatform(navigator);

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

function pageShell(content, signedIn = false, { home = false } = {}) {
  const beta = account?.beta ?? {};
  const unread = Math.max(0, Number(beta.unreadFeedback) || 0);
  const adminLink = signedIn && beta.isAdmin
    ? `<a href="/admin">Admin${unread ? `<span class="nav-count" aria-label="${unread} unread feedback messages">${unread}</span>` : ""}</a>`
    : "";
  const links = home
    ? `<span class="lp-nav-links">${landingNavLinks()}</span><button class="nav-button" id="google-sign-in">Sign in</button><a class="lp-nav-cta" href="#pricing">Get DittyBag</a>`
    : `<a href="/about">About</a><a href="/secure">Secure</a><a href="/download">Download</a>${adminLink}${signedIn ? '<button class="nav-button" id="sign-out">Sign out</button>' : ""}`;
  return `<div class="site-shell${home ? " is-home" : ""}"><nav aria-label="Main navigation"><a class="brand" href="/" aria-label="DittyBag Pro home"><img src="/brand/dittybag-icon.png" alt=""><span class="brand-name"><strong>DittyBag</strong><small>PRO</small></span></a><div>${links}</div></nav>${content}<footer><span>DittyBag Pro Web ${releaseVersion} · Encrypted sync</span><span class="footer-links"><a href="/terms">Terms of Service</a><a href="/privacy">Privacy Policy</a><span>Local-first by design · Cloud sync is optional</span></span></footer></div>`;
}

function showPublicPage(title, content, signedIn = false) {
  document.title = `${title} · DittyBag Pro`;
  app.innerHTML = pageShell(`<main class="public-page">${content}</main>`, signedIn);
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
}

function downloadActions() {
  return `<div class="download-actions" data-download-platform="all"><a class="primary-button" href="/download/releases/${releaseVersion}/macos">Download macOS ${releaseVersion} (DMG)</a><a class="primary-button" href="/download/releases/${releaseVersion}/windows">Download Windows ${releaseVersion} (NSIS)</a><small>DittyBag ${releaseVersion} · Apple silicon Mac and Windows. Downloads become available together after release approval.</small></div>`;
}

function aboutPage(signedIn = false) {
  showPublicPage(
    "About",
    `<header class="public-hero"><p class="eyebrow">ABOUT DITTYBAG PRO</p><h1>A fast clipboard home,<br><em>built to stay out of your way.</em></h1><p>DittyBag Pro brings clipboard history, screenshots, markup, OCR, recording, and encrypted device sync into one focused native app.</p></header>
    <section class="about-section"><div><p class="eyebrow">LOCAL FIRST</p><h2>Useful before you ever create an account.</h2></div><div><p>Capture, search, paste, annotate, and organize on your computer without signing in. An account is optional and adds encrypted recall across your trusted computers and the web.</p><p>The product is being built around one rule: everyday actions should stay immediate and understandable, even as Pro becomes more capable.</p></div></section>
    <section class="public-grid"><article><span>01</span><h2>Clipboard, accelerated</h2><p>Quick History appears near your pointer, while the complete History window supports search, favorites, filters, and cleanup.</p></article><article><span>02</span><h2>Capture without friction</h2><p>Grab an area straight to the clipboard or open Capture Studio for movable markup layers, OCR, redaction, and export.</p></article><article><span>03</span><h2>Your devices, one history</h2><p>Pair trusted computers to the same account and choose which devices appear in Quick History on each installation.</p></article></section>`,
    signedIn,
  );
}

function securePage(signedIn = false) {
  showPublicPage(
    "Secure",
    `<header class="public-hero"><p class="eyebrow">SECURE BY DESIGN</p><h1>Your clipboard content is encrypted<br><em>before it leaves your device.</em></h1><p>DittyBag combines end-to-end encrypted sync with clear account controls and honest explanations of the metadata the service still needs.</p></header>
    <section class="security-section"><header><div><p class="eyebrow">HOW IT WORKS</p><h2>Security that protects the product without hiding the boundaries.</h2></div><span class="security-badge">Private beta</span></header><div class="security-grid"><article><span>01</span><h3>Private content</h3><p>Synced clipboard text, links, and images are encrypted on your device. DittyBag’s cloud receives encrypted data, not the recovery key needed to read it.</p></article><article><span>02</span><h3>Protected access</h3><p>Google sign-in, optional authenticator 2FA, one-time recovery codes, trusted-device approval, and device revocation protect account access.</p></article><article><span>03</span><h3>Keys stay with you</h3><p>The desktop app protects sync credentials in the operating system credential store. After you unlock Web History, its recovery key is kept only in the current tab’s temporary session storage until your timer expires, you lock or sign out, or the tab session ends.</p></article><article><span>04</span><h3>Limited visible metadata</h3><p>The service keeps limited account, device, timing, content category, and size information needed to authenticate, route sync, prevent abuse, and enforce storage limits.</p></article></div><aside><strong>Keep your recovery key private.</strong><span>Anyone who has it and access to your encrypted history may be able to read that history. Revoking a computer blocks future sync access, but cannot erase copies already stored on that computer.</span></aside></section>
    <section class="secure-details"><article><h2>What encryption does protect</h2><p>The contents of synced clipboard events and cloud images. A cloud storage operator or database reader should see ciphertext rather than the original content.</p></article><article><h2>What it does not protect</h2><p>An unlocked device, content after you copy it into another app, someone with both your recovery key and account access, or limited operational metadata described in the Privacy Policy.</p></article><article><h2>Your part</h2><p>Protect your Google account and recovery key, enable 2FA, use device encryption, and revoke computers you no longer control.</p></article></section>`,
    signedIn,
  );
}

function downloadPage(signedIn = false) {
  showPublicPage(
    "Download",
    `<header class="public-hero"><p class="eyebrow">DOWNLOAD DITTYBAG PRO</p><h1>Native speed on your desktop.</h1><p>Use DittyBag locally without an account, or sign in after installation to connect encrypted History across your trusted devices.</p></header>
    <section class="download download-page"><div><p class="eyebrow">DESKTOP BETA</p><h2>Choose your computer.</h2><p>Each person signs in with their own DittyBag account. Save your work and quit the app before replacing it. Screen Recording and other permissions are managed by macOS; an update cannot approve them for you.</p></div>${downloadActions()}</section>
    ${macInstallGuide({ version: releaseVersion, format: "dmg" })}
    <section class="public-grid download-notes"><article><span>MAC</span><h2>macOS</h2><p>Apple silicon · ${releaseVersion} tester release. The release pipeline requires Developer ID signing and Apple notarization before publishing. Capture features require your permission.</p></article><article><span>WIN</span><h2>Windows</h2><p>The Windows beta shares the same local-first History, capture, keybind, and sync foundation. Some platform-specific features may arrive on a different schedule.</p></article><article><span>SYNC</span><h2>Optional account</h2><p>Cloud sync is never required for local clipboard History. Sign in only when you want encrypted recall on another device or in a browser tab.</p></article><article aria-label="Known export issue"><span>VIDEO</span><h2>Known issue</h2><p>Some 4K video exports fail intermittently. Local export statistics in Video Studio show failures and completed attempts; cancellations are excluded. Rates cover recorded exports on that device, not all users or past exports.</p></article></section>`,
    signedIn,
  );
}

function termsPage(signedIn = false) {
  showPublicPage(
    "Terms of Service",
    `<article class="legal-page"><header><p class="eyebrow">LEGAL</p><h1>Terms of Service</h1><p class="legal-date">Effective and last updated August 26, 2026</p><p>These Terms govern your use of the DittyBag desktop applications, website, account, downloads, and encrypted synchronization service (collectively, the “Service”). By using the Service, you agree to these Terms.</p></header>
    <section><h2>1. Who may use DittyBag</h2><p>You must be able to enter into a binding agreement where you live. If you use DittyBag for an organization, you represent that you are authorized to accept these Terms for it. The Service is not directed to children under 13.</p></section>
    <section><h2>2. Accounts and security</h2><p>Local desktop features do not require an account. Optional sync uses Google sign-in, and may use authenticator-based two-factor authentication. You are responsible for activity through your account, for protecting your devices and recovery key, and for promptly revoking any device you no longer control.</p></section>
    <section><h2>3. Your content</h2><p>You retain ownership of content you capture, copy, upload, or sync. You give DittyBag only the limited permission needed to receive, store, transmit, and delete encrypted data and related service metadata to operate the features you request. You must have the right to use the content and must not use the Service to violate another person’s rights or applicable law.</p></section>
    <section><h2>4. Acceptable use</h2><p>Do not misuse the Service, interfere with its operation, bypass security or storage limits, probe accounts or devices you do not own, distribute malware, automate abusive traffic, or use DittyBag to facilitate illegal activity. You may not resell access to the hosted Service without written permission. Restrictions do not limit rights that applicable law says cannot be restricted.</p></section>
    <section><h2>5. Encryption and recovery</h2><p>DittyBag is designed so synced clipboard content is encrypted before upload and the hosted service does not receive your recovery key. That design also means we may be unable to recover encrypted content if you lose the key. Revoking a device stops future authorized sync; it cannot remotely erase content already copied to that device or another application.</p></section>
    <section><h2>6. Storage, availability, and beta features</h2><p>Cloud sync is optional and subject to displayed storage, item, retention, and usage limits. Items may be removed as those limits are enforced. Keep independent copies of anything important. Beta features may change, be unavailable, or contain defects, and we may modify or discontinue features while the product develops.</p></section>
    <section><h2>7. Software license and updates</h2><p>We grant you a personal, limited, non-exclusive, non-transferable, revocable license to install and use the DittyBag applications for their intended purpose. Updates may be required for compatibility or security. Except where law permits otherwise, you may not copy, sell, lease, circumvent, or reverse engineer the Service.</p></section>
    <section><h2>8. Third-party services</h2><p>DittyBag relies on services including Google for sign-in and Cloudflare for hosting, databases, object storage, delivery, and security. Your use of those providers may also be governed by their terms and privacy practices. We are not responsible for third-party services outside our control.</p></section>
    <section><h2>9. Suspension and termination</h2><p>You may stop using DittyBag at any time. We may suspend or terminate hosted access when reasonably necessary to protect users or the Service, respond to legal requirements, prevent abuse, or address a material breach of these Terms. Contact us to request account deletion; identity verification may be required.</p></section>
    <section><h2>10. Disclaimers and liability</h2><p>To the fullest extent permitted by law, the Service is provided “as is” and “as available,” without warranties of uninterrupted operation, fitness for a particular purpose, or non-infringement. DittyBag is not a backup service. To the fullest extent permitted by law, DittyBag and its operators will not be liable for indirect, incidental, special, consequential, or punitive damages, or for loss of data, profits, or business arising from the Service. Nothing in these Terms excludes rights or liability that applicable law does not allow us to exclude.</p></section>
    <section><h2>11. Changes and contact</h2><p>We may update these Terms as the Service changes. We will post the updated date and provide additional notice when required by law. For legal questions or account-deletion requests, contact DittyBag through <a href="https://denominatorx.com">DenominatorX</a>.</p></section></article>`,
    signedIn,
  );
}

function privacyPage(signedIn = false) {
  showPublicPage(
    "Privacy Policy",
    `<article class="legal-page"><header><p class="eyebrow">PRIVACY</p><h1>Privacy Policy</h1><p class="legal-date">Effective and last updated August 26, 2026</p><p>This Policy explains what DittyBag Pro collects, why it is used, when it is shared, and the choices available to you. Local-only desktop use and optional encrypted sync have different data flows, described below.</p></header>
    <section><h2>1. Information we collect</h2><h3>Account information</h3><p>When you sign in with Google, we receive account identifiers and profile details such as your name, email address, email-verification status, and profile image. We store authentication records, linked-provider information, session identifiers, session expiration, IP address, and browser or device user-agent information.</p><h3>Trusted-device and service metadata</h3><p>For paired computers we store a device name, platform, application version, device public key, credential hash, creation and last-seen times, sync timing, and revocation state. For synced events we store identifiers, ordering and timing, originating device, encrypted size, integrity values, and a broad content category such as image or other so the service can route data and enforce limits.</p><h3>Encrypted clipboard content</h3><p>If you enable sync, the service stores encrypted clipboard events and encrypted image objects. The recovery key needed to decrypt that content is created and retained by your trusted devices; it is not sent to or stored by the DittyBag service. When you unlock Web History, decryption occurs in the browser. To remain unlocked across a refresh, the recovery key is temporarily kept in that tab’s session storage until the displayed timer expires, you lock or sign out, or the tab session ends.</p><h3>Local-only information</h3><p>Your desktop History, local thumbnails, preferences, exclusions, markup projects, and other local data remain on your computer unless a feature explicitly syncs an item. Your operating system and applications may separately process content you copy or capture.</p></section>
    <section><h2>2. How we use information</h2><p>We use information to authenticate you; administer private-beta invitations; provide optional encrypted synchronization and Web History; pair, identify, and revoke trusted devices; receive and respond to feedback you choose to submit; enforce storage and security limits; prevent abuse; troubleshoot reliability; deliver application downloads; and comply with legal obligations. Feedback is visible to authorized DittyBag administrators and may generate a private notification to the configured administrator mailbox. We do not use clipboard content for advertising or train AI models on it.</p></section>
    <section><h2>3. Cookies and similar technologies</h2><p>The website uses essential cookies and browser storage for sign-in sessions, optional second-factor state, security, and requested product functionality. DittyBag does not currently use advertising cookies or cross-site behavioral tracking.</p></section>
    <section><h2>4. When information is disclosed</h2><p>We disclose information to service providers that operate DittyBag on our behalf, principally Google for authentication and Cloudflare for hosting, storage, content delivery, and security. We may also disclose information when required by law, to protect users or the Service, in connection with a business reorganization, or with your direction. We do not sell personal information or share it for cross-context behavioral advertising.</p></section>
    <section><h2>5. Storage and retention</h2><p>Account records are kept while your account is active and as reasonably necessary for security, legal, and operational purposes. Authentication sessions are designed to expire, and short-lived pairing and abuse-prevention records are cleaned up after their operational purpose. Encrypted cloud History is retained until deleted, replaced by synchronized changes, removed under the displayed retention limits, or removed with the associated account. The current beta limits cloud storage to 50 images and 100 MB of encrypted data per account. Local History follows the settings on each computer and is not erased by cloud cleanup.</p></section>
    <section><h2>6. Security</h2><p>DittyBag uses encrypted transport, end-to-end encrypted clipboard payloads, hashed device credentials, protected operating-system credential storage, access controls, rate limits, optional authenticator 2FA, trusted-device approval, device revocation, and security-focused response headers. No system can guarantee absolute security. Protect your recovery key and device login, and notify us if you believe your account has been compromised.</p></section>
    <section><h2>7. Your choices and rights</h2><p>You can use the desktop app without cloud sync, choose which computers are trusted, revoke a computer, lock Web History by closing or locking the tab, manage 2FA, control local retention, and remove local History. Depending on where you live, you may have rights to request access, correction, deletion, portability, restriction, or information about disclosures. Contact us to make a privacy or account-deletion request; we may need to verify your identity.</p></section>
    <section><h2>8. Children, international use, and changes</h2><p>DittyBag is not directed to children under 13 and we do not knowingly collect their personal information. The Service may process information in the United States and other places where its providers operate. We may update this Policy as features or legal requirements change; the updated date will appear above, with additional notice when required.</p></section>
    <section><h2>9. Contact</h2><p>For privacy questions, rights requests, or account deletion, contact DittyBag through <a href="https://denominatorx.com">DenominatorX</a>. Please do not send a recovery key or clipboard content with your request.</p></section></article>`,
    signedIn,
  );
}

const publicPages = {
  "/about": aboutPage,
  "/secure": securePage,
  "/download": downloadPage,
  "/terms": termsPage,
  "/privacy": privacyPage,
};

let landingMedia = null;

function landing(authError = "") {
  document.title = "DittyBag · Copy once. Find it forever.";
  const errorNotice = authError
    ? `<div class="auth-error" role="alert"><strong>Google sign-in didn’t finish.</strong><span>${escapeHtml(authError)}</span></div>`
    : "";
  app.innerHTML = pageShell(
    `<main class="landing lp">${landingMarkup({ errorNotice, version: releaseVersion, platform: visitorPlatform, media: landingMedia, downloadActions: downloadActions() })}</main>`,
    false,
    { home: true },
  );
  document.querySelector("#google-sign-in")?.addEventListener("click", signIn);
  const main = document.querySelector("main.landing");
  startLandingMotion(main);
  // Real captures from the app replace the illustrations once they're published.
  if (!landingMedia) {
    loadMediaManifest().then((media) => {
      landingMedia = media;
      const hasMedia = Object.keys(media.chapters).length || media.screenshots.length;
      if (hasMedia && main.isConnected) landing(authError);
    });
  }
}

function inviteTokenFromLocation() {
  const match = location.pathname.match(/^\/invite\/([A-Za-z0-9_-]{43})$/);
  return match?.[1] ?? new URLSearchParams(location.search).get("token") ?? "";
}

async function inviteLanding(signedIn = false) {
  document.title = "Beta invitation · DittyBag Pro";
  app.innerHTML = pageShell(`<main class="enrollment invite-page"><section aria-busy="true"><p class="eyebrow">PRIVATE BETA</p><h1>Checking your invitation…</h1><p>Please wait a moment.</p></section></main>`, signedIn);
  const token = inviteTokenFromLocation();
  if (!/^[A-Za-z0-9_-]{43}$/.test(token)) {
    app.innerHTML = pageShell(`<main class="enrollment invite-page"><section><p class="eyebrow">PRIVATE BETA</p><h1>This invitation isn’t available.</h1><p>The link may be incomplete, expired, or already used. Ask the person who invited you for a new link.</p><a class="secondary-button" href="/">Back to DittyBag</a></section></main>`, signedIn);
    return;
  }
  const response = await fetch("/api/invites/accept", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", "x-dittybag-request": "web" },
    body: JSON.stringify({ token }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    app.innerHTML = pageShell(`<main class="enrollment invite-page"><section><p class="eyebrow">PRIVATE BETA</p><h1>This invitation isn’t available.</h1><p>${response.status >= 500 ? "DittyBag could not check this invitation right now. Please try again." : "The link may have expired, been revoked, or already been used. Ask the person who invited you for a new link."}</p><button class="secondary-button" id="retry-invite">Try again</button></section></main>`, signedIn);
    document.querySelector("#retry-invite")?.addEventListener("click", () => inviteLanding(signedIn));
    return;
  }
  const emailHint = result.emailHint ? `<p class="invite-hint">This invitation is reserved for <strong>${escapeHtml(result.emailHint)}</strong>. Choose the matching Google account.</p>` : "";
  app.innerHTML = pageShell(`<main class="enrollment invite-page"><section><p class="eyebrow">YOU’RE INVITED</p><h1>Help shape DittyBag.</h1><p>Join the private beta to test the product, explore encrypted sync, and send feedback directly from your account.</p>${emailHint}<div class="invite-actions">${signedIn ? '<a class="primary-button" href="/app">Open DittyBag</a>' : '<button class="google-button" id="invite-google-sign-in"><span aria-hidden="true">G</span> Continue with Google</button>'}</div><small>Invitations provide testing access and may expire. Your Google email is used only for your DittyBag account.</small></section></main>`, signedIn);
  document.querySelector("#invite-google-sign-in")?.addEventListener("click", signIn);
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
}

function clipCopiedAt(clip) {
  return Number(clip.lastUsedAt ?? clip.createdAt ?? clip.syncedAt ?? 0);
}

function clipDeviceNames(clip) {
  const origins = Array.isArray(clip.copyOrigins) ? clip.copyOrigins : [];
  const names = origins.map((origin) => origin.name).filter(Boolean);
  if (!names.length && clip.originDeviceName) names.push(clip.originDeviceName);
  return [...new Set(names)].join(", ") || "Connected computer";
}

function clipMetadata(clip) {
  const source = clip.sourceApp || "Clipboard";
  const deviceNames = clipDeviceNames(clip);
  const copiedAt = clipCopiedAt(clip);
  const time = copiedAt ? `${formatSeen(copiedAt)} · ${new Date(copiedAt).toLocaleString()}` : "Time unavailable";
  const repeats = Number(clip.count) > 1 ? ` · copied ${Number(clip.count)}×` : "";
  return `${escapeHtml(source)} · ${escapeHtml(deviceNames)} · ${escapeHtml(time)}${repeats}`;
}

function historyClipView(clip, index) {
  const imageUrl = safeWebImageUrl(clip.webImageUrl);
  if (clip.kind === "image" && imageUrl) {
    return `<article class="web-clip image-clip" data-clip-index="${index}"><button class="web-clip-image" data-clip-index="${index}" aria-label="Expand screenshot"><img src="${escapeHtml(imageUrl)}" alt="Synced screenshot"></button><div><strong>${escapeHtml(clip.text || "Screenshot")}</strong><small>${clipMetadata(clip)}</small></div><button class="copy-web-clip" data-clip-index="${index}" aria-label="Copy screenshot">Copy</button><a class="save-web-clip" href="${escapeHtml(imageUrl)}" download="dittybag-image-${index + 1}">Save</a></article>`;
  }
  const text = String(clip.text ?? "");
  return `<article class="web-clip" data-clip-index="${index}"><b>${clip.kind === "link" || clip.kind === "url" ? "↗" : "T"}</b><div><strong>${escapeHtml(text.slice(0, 180) || "Empty clip")}</strong><small>${clipMetadata(clip)}</small></div><button class="copy-web-clip" data-clip-index="${index}" aria-label="Copy clipboard item">Copy</button></article>`;
}

function webHistoryView() {
  if (account?.entitlement && !account.entitlement.hosted) return `<section class="web-history-card"><header><div><p class="eyebrow">DITTYBAG PRO+</p><h2>Encrypted sync and Web History</h2></div><span class="status">Not included</span></header><div class="history-unlock"><b>＋</b><div><strong>Your local History is unchanged</strong><p>Free and Pro keep clipboard data on this computer. Pro+ adds end-to-end encrypted device sync, browser access, hosted storage, and metered AI tools.</p></div></div></section>`;
  if (!webHistory.unlocked) return `<section class="web-history-card"><header><div><p class="eyebrow">ENCRYPTED HISTORY</p><h2>Your clipboard on the web</h2></div><span class="status">Locked locally</span></header><div class="history-unlock"><b>⌘</b><div><strong>Enter your DittyBag recovery key</strong><p>Decryption happens in this browser. To survive a refresh, the key stays only in this tab’s temporary session storage until you lock, sign out, close the tab session, or the timer expires. It is never sent to DittyBag.</p><label>Recovery key<input id="history-recovery-key" type="password" autocomplete="off" placeholder="CSP1-…"></label><label>Keep this tab unlocked<select id="history-unlock-duration"><option value="15">15 minutes</option><option value="60" selected>1 hour</option><option value="240">4 hours</option></select></label><button class="primary-button" id="unlock-history" ${webHistory.loading ? "disabled" : ""}>${webHistory.loading ? "Preparing encrypted History…" : "Unlock History"}</button><p class="form-error">${escapeHtml(webHistory.error)}</p></div></div></section>`;
  const clips = webHistory.clips.slice(0, 50);
  const remaining = formatHistoryUnlockRemaining(webHistory.expiresAt);
  return `<section class="web-history-card"><header><div><p class="eyebrow">ENCRYPTED HISTORY</p><h2>Clipboard History</h2></div><span class="status sync-live" id="history-unlock-status">Unlocked · <span id="history-lock-countdown">${remaining}</span></span></header><p class="cipher-note">Showing ${clips.length} unique recent items. Repeat copies are grouped at their newest position. Click a row to copy it; click an image to expand it.</p><div class="web-history-list">${clips.length ? clips.map(historyClipView).join("") : '<div class="empty-devices">No synced History items yet.</div>'}</div><button class="secondary-button" id="lock-history">Lock web History</button></section>`;
}

function formatSeen(value) {
  if (!value) return "Not synced yet";
  const seconds = Math.max(0, Math.round((Date.now() - Number(value)) / 1000));
  if (seconds < 60) return "Active now";
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return new Date(Number(value)).toLocaleDateString();
}

function deviceRows() {
  if (!devices.length)
    return '<div class="empty-devices">No computers paired yet. Start pairing from DittyBag Pro.</div>';
  return devices
    .map(
      (device) =>
        `<div class="web-device"><span class="device-glyph">${device.platform === "macOS" ? "⌘" : "▣"}</span><span><strong>${escapeHtml(device.name)}</strong><small>${escapeHtml(device.platform)} · ${escapeHtml(device.appVersion)} · ${formatSeen(device.lastSeenAt)}</small></span><button class="revoke-device" data-device-id="${escapeHtml(device.id)}">Revoke</button></div>`,
    )
    .join("");
}

function feedbackCard() {
  if (account?.beta?.canSubmitFeedback === false) return "";
  return `<section class="feedback-card" aria-labelledby="feedback-title"><div><p class="eyebrow">BETA FEEDBACK</p><h2 id="feedback-title">Tell us what you think</h2><p>Your note goes privately to the DittyBag team. Please don’t include recovery keys, passwords, or sensitive clipboard content.</p></div><form id="feedback-form"><label for="feedback-category">What kind of feedback is this?</label><select id="feedback-category" name="category"><option value="experience">General experience</option><option value="bug">Something isn’t working</option><option value="request">Feature idea</option><option value="other">Other</option></select><label for="feedback-message">Your feedback</label><textarea id="feedback-message" name="message" required maxlength="4000" rows="6" aria-describedby="feedback-help feedback-count feedback-error"></textarea><div class="field-meta"><small id="feedback-help">Include what you expected and what happened, if relevant.</small><small id="feedback-count" aria-live="polite">0 / 4000</small></div><button class="primary-button" type="submit">Send feedback</button><p class="form-error" id="feedback-error" role="alert"></p><p class="form-success" id="feedback-success" role="status" aria-live="polite"></p></form></section>`;
}

function dashboard() {
  const user = account.user;
  const usage = account.sync.usage;
  const entitlement = account.entitlement ?? { tier: "free", hosted: false, deviceLimit: 1 };
  const planLabel = ({ free: "DittyBag", pro: "DittyBag Pro", pro_plus: "DittyBag Pro+", pro_plus_beta: "DittyBag Pro+ Beta" })[entitlement.tier] ?? "DittyBag";
  const maximumStorage = usage ? `${usage.maximumEncryptedBytes >= 1_000_000_000 ? `${(usage.maximumEncryptedBytes / 1_000_000_000).toFixed(0)} GB` : `${(usage.maximumEncryptedBytes / 1_000_000).toFixed(0)} MB`}` : "—";
  const usageLabel = usage && entitlement.hosted ? `${usage.images}/${usage.maximumImages} images · ${(usage.encryptedBytes / 1_000_000).toFixed(1)} MB/${maximumStorage}` : "Local storage only";
  app.innerHTML = pageShell(
    `<main class="dashboard"><header class="account-header"><div><p class="eyebrow">DITTYBAG ACCOUNT · ${escapeHtml(planLabel)}</p><h1>Welcome back, ${escapeHtml(user.name?.split(" ")[0] || "there")}.</h1><p>${entitlement.hosted ? "Your trusted computers exchange end-to-end encrypted clipboard events. DittyBag’s server never receives the key." : "Your plan keeps clipboard History on each computer. Nothing is uploaded unless you activate DittyBag Pro+."}</p></div>${user.image ? `<img src="${escapeHtml(user.image)}" alt="">` : ""}</header><section class="metrics"><article><span>Plan</span><strong>${escapeHtml(planLabel)}</strong><small>${entitlement.hosted ? `Up to ${entitlement.deviceLimit} trusted devices` : "Local-first, no hosted usage"}</small></article><article><span>Encrypted events</span><strong>${account.sync.encryptedItems}</strong><small>${escapeHtml(usageLabel)}</small></article><article><span>Account protection</span><strong>${account.security.twoFactorEnabled ? "2FA on" : "Google"}</strong><small>${account.security.twoFactorEnabled ? "Authenticator protected" : "Add an authenticator below"}</small></article></section>${webHistoryView()}<section class="dashboard-grid"><article class="library-card"><header><div><p class="eyebrow">TRUSTED DEVICES</p><h2>Connected computers</h2></div><span class="status ${entitlement.hosted ? "sync-live" : ""}">${entitlement.hosted ? "Encrypted sync online" : "Local only"}</span></header><div class="web-devices">${deviceRows()}</div><p class="cipher-note">${entitlement.hosted ? `The cloud keeps encrypted objects plus limited routing, retention, and usage metadata. Your current limit is ${usage.maximumImages} cloud images and ${maximumStorage}.` : "DittyBag and DittyBag Pro do not use hosted storage. Existing local History stays on each computer."}</p></article><article class="security-card"><p class="eyebrow">SECURITY</p><h2>Two-factor authentication</h2>${account.security.twoFactorEnabled ? '<div class="security-ready"><b>✓</b><span><strong>Authenticator enabled</strong><small>Recovery codes were created during setup.</small></span></div><button class="danger-button" id="disable-2fa">Disable 2FA</button>' : '<p>Add a code from 1Password, Google Authenticator, Authy, or another TOTP app after Google sign-in.</p><button class="primary-button" id="enable-2fa">Set up authenticator</button>'}<hr><strong class="account-email">${escapeHtml(user.email)}</strong><small>Google is the linked sign-in provider.</small></article></section>${feedbackCard()}</main>`,
    true,
  );
  bindAccountActions();
  bindHistoryActions();
  bindFeedbackActions();
}

function formatAdminDate(value) {
  const time = Number(value);
  return time ? new Date(time).toLocaleString() : "—";
}

function adminInviteRows() {
  if (!adminInvites.length) return '<div class="admin-empty"><strong>No invites yet</strong><span>Create an invitation to add your first beta tester.</span></div>';
  return adminInvites.map((invite) => `<article class="admin-row"><div><strong>${escapeHtml(invite.emailHint || "Anyone with the link")}</strong><small>Created ${escapeHtml(formatAdminDate(invite.createdAt))} · Expires ${escapeHtml(formatAdminDate(invite.expiresAt))}</small>${Number(invite.missingAccounts) > 0 ? `<small class="form-error">Review: ${Number(invite.missingAccounts)} redemption(s) over 15 minutes old have no matching account. Signup may have failed or the account was deleted. Uses have not been refunded.</small>` : ""}</div><span class="status status-${escapeHtml(invite.status)}">${escapeHtml(invite.status)}</span><span>${Number(invite.uses) || 0} / ${Number(invite.maxUses) || 1} used</span>${invite.status === "active" ? `<button class="danger-button revoke-invite" type="button" data-invite-id="${escapeHtml(invite.id)}">Revoke</button>` : ""}</article>`).join("");
}

function adminFeedbackRows() {
  if (!adminFeedback.length) return `<div class="admin-empty"><strong>${adminFeedbackFilter === "all" ? "No feedback yet" : "You’re all caught up"}</strong><span>${adminFeedbackFilter === "all" ? "Tester feedback will appear here and in the configured admin email inbox." : `There are no ${escapeHtml(adminFeedbackFilter)} messages.`}</span></div>`;
  return adminFeedback.map((item) => `<article class="admin-feedback-row"><header><div><strong>${escapeHtml(item.reporter || "Beta tester")}</strong><small>${escapeHtml(formatAdminDate(item.createdAt))} · ${escapeHtml(item.category)}</small></div><label for="feedback-status-${escapeHtml(item.id)}">Status<span class="sr-only"> for feedback from ${escapeHtml(item.reporter || "beta tester")}</span></label><select id="feedback-status-${escapeHtml(item.id)}" class="feedback-status" data-feedback-id="${escapeHtml(item.id)}" data-original-status="${escapeHtml(item.status)}"><option value="new" ${item.status === "new" ? "selected" : ""}>New</option><option value="reviewing" ${item.status === "reviewing" ? "selected" : ""}>Reviewing</option><option value="resolved" ${item.status === "resolved" ? "selected" : ""}>Resolved</option><option value="archived" ${item.status === "archived" ? "selected" : ""}>Archived</option></select></header><p>${escapeHtml(item.message)}</p></article>`).join("");
}

function adminDashboard() {
  document.title = "Beta admin · DittyBag Pro";
  app.innerHTML = pageShell(`<main class="dashboard admin-dashboard"><header class="account-header"><div><p class="eyebrow">PRIVATE BETA ADMIN</p><h1>Invites and feedback</h1><p>Invite testers, review their notes, and keep access intentionally small while DittyBag is in beta.</p></div></header><section class="admin-panel" aria-labelledby="invite-admin-title"><header><div><p class="eyebrow">ACCESS</p><h2 id="invite-admin-title">Signup invitations</h2></div></header><form id="invite-form" class="admin-form"><div class="admin-field"><label for="invite-email">Reserve for an email <span>(optional)</span></label><input id="invite-email" name="email" type="email" autocomplete="off" placeholder="tester@example.com"></div><div class="admin-field"><label for="invite-uses">Uses</label><select id="invite-uses" name="maxUses"><option value="1">One person</option><option value="5">Up to 5 people</option><option value="10">Up to 10 people</option></select></div><div class="admin-field"><label for="invite-expiry">Expires</label><select id="invite-expiry" name="expiresInDays"><option value="7">In 7 days</option><option value="14">In 14 days</option><option value="30">In 30 days</option></select></div><button class="primary-button" type="submit">Create invite</button><p class="form-error" id="invite-error" role="alert"></p></form><div class="invite-result" id="invite-result" role="status" aria-live="polite"></div><div class="admin-list" id="invite-list">${adminInviteRows()}</div></section><section class="admin-panel" aria-labelledby="feedback-inbox-title"><header><div><p class="eyebrow">NOTIFICATIONS</p><h2 id="feedback-inbox-title">Tester feedback</h2></div><div class="admin-filters" role="group" aria-label="Filter tester feedback">${["all", "new", "reviewing", "resolved", "archived"].map((status) => `<button type="button" class="filter-feedback ${adminFeedbackFilter === status ? "active" : ""}" data-status="${status}" aria-pressed="${adminFeedbackFilter === status}">${status}</button>`).join("")}</div></header><p class="admin-note">New feedback is also queued for the configured private admin email. Testers never see that address.</p><div class="admin-list" id="feedback-list">${adminFeedbackRows()}</div><p class="form-error" id="admin-feedback-error" role="alert"></p></section></main>`, true);
  renderAdminFeedbackPage();
  bindAdminActions();
}

function renderAdminFeedbackPage() {
  const list = document.querySelector("#feedback-list");
  if (!list) return;
  list.innerHTML = adminFeedbackRows();
  document.querySelectorAll(".filter-feedback").forEach(button => {
    const active = button.dataset.status === adminFeedbackFilter;
    button.classList.toggle("active", active); button.setAttribute("aria-pressed", String(active));
  });
  let pager = document.querySelector("#feedback-pagination");
  if (!pager) { pager = document.createElement("nav"); pager.id = "feedback-pagination"; pager.className = "admin-filters"; pager.setAttribute("aria-label", "Feedback pages"); list.after(pager); }
  pager.innerHTML = `<button type="button" data-feedback-page="newer" ${adminFeedbackCursors.length <= 1 ? "disabled" : ""}>Newer</button><span role="status">Page ${adminFeedbackCursors.length} · ${adminFeedback.length} reports</span><button type="button" data-feedback-page="older" ${!adminFeedbackNextCursor ? "disabled" : ""}>Older</button><button type="button" data-feedback-page="first">Refresh newest</button>`;
  pager.querySelectorAll("button").forEach(button => button.addEventListener("click", () => refreshAdminFeedback(button)));
  const error = document.querySelector("#admin-feedback-error"); if (error) error.textContent = "";
  bindAdminRowActions();
}

async function loadAdminDashboard() {
  app.innerHTML = pageShell('<main class="dashboard admin-dashboard"><div class="admin-empty" aria-busy="true"><strong>Loading beta admin…</strong></div></main>', true);
  const loading = document.querySelector(".admin-empty");
  ++adminFeedbackRequest;
  try {
  const [inviteResponse, feedbackResponse] = await Promise.all([
    fetch("/api/admin/invites", { credentials: "include" }),
    fetch(`/api/admin/feedback?status=${encodeURIComponent(adminFeedbackFilter)}`, { credentials: "include" }),
  ]);
  if (!inviteResponse.ok || !feedbackResponse.ok) throw new Error("Admin load rejected");
  const [invites, feedback] = await Promise.all([inviteResponse.json(), feedbackResponse.json()]);
  if (!Array.isArray(invites.invites) || !Array.isArray(feedback.feedback)) throw new Error("Invalid admin response");
  if (feedback.nextCursor != null && (typeof feedback.nextCursor !== "string" || !feedback.nextCursor.length || feedback.nextCursor.length > 200)) throw new Error("Invalid feedback cursor");
  if (!loading?.isConnected) return;
  adminInvites = invites.invites;
  adminFeedback = feedback.feedback;
  adminFeedbackCursors = [null];
  adminFeedbackNextCursor = feedback.nextCursor ?? null;
  adminDashboard();
  } catch {
    if (!loading?.isConnected) return;
    app.innerHTML = pageShell('<main class="enrollment"><section><p class="eyebrow">BETA ADMIN</p><h1>The admin dashboard could not load.</h1><p>Your account is still safe. Try loading the dashboard again.</p><button class="secondary-button" id="retry-admin">Try again</button></section></main>', true);
    document.querySelector("#retry-admin")?.addEventListener("click", loadAdminDashboard);
    document.querySelector("#sign-out")?.addEventListener("click", signOut);
    return;
  }
}

async function refreshAdminFeedback(button) {
  if (button.disabled) return;
  const request = ++adminFeedbackRequest, status = button.dataset.status ?? adminFeedbackFilter;
  let cursors = [null];
  if (button.dataset.feedbackPage === "older") {
    if (!adminFeedbackNextCursor) return;
    cursors = [...adminFeedbackCursors, adminFeedbackNextCursor];
  } else if (button.dataset.feedbackPage === "newer") cursors = adminFeedbackCursors.slice(0, -1);
  if (!cursors.length) return;
  const cursor = cursors.at(-1);
  try {
    const response = await fetch(`/api/admin/feedback?status=${encodeURIComponent(status)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`, { credentials: "include" });
    if (!response.ok) throw new Error("Feedback refresh rejected");
    const result = await response.json();
    if (!Array.isArray(result.feedback)) throw new Error("Invalid feedback response");
    if (result.nextCursor != null && (typeof result.nextCursor !== "string" || !result.nextCursor.length || result.nextCursor.length > 200)) throw new Error("Invalid feedback cursor");
    if (request !== adminFeedbackRequest || !button.isConnected) return;
    adminFeedbackFilter = status;
    adminFeedback = result.feedback;
    adminFeedbackCursors = cursors;
    adminFeedbackNextCursor = result.nextCursor ?? null;
    renderAdminFeedbackPage();
  } catch {
    if (request !== adminFeedbackRequest || !button.isConnected) return;
    const error = document.querySelector("#admin-feedback-error");
    if (error) error.textContent = "Feedback could not be refreshed. Your previous results are still shown; try again.";
  }
}

function bindAdminActions() {
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
  document.querySelector("#invite-form")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const submit = form.querySelector('button[type="submit"]');
    const error = document.querySelector("#invite-error");
    const email = document.querySelector("#invite-email")?.value.trim() ?? "";
    const body = {
      ...(email ? { email } : {}),
      maxUses: Number(document.querySelector("#invite-uses")?.value) || 1,
      expiresInDays: Number(document.querySelector("#invite-expiry")?.value) || 7,
    };
    error.textContent = "";
    submit.disabled = true;
    submit.textContent = "Creating…";
    try {
      const response = await fetch("/api/admin/invites", {
        method: "POST", credentials: "include",
        headers: { "content-type": "application/json", "x-dittybag-request": "web" },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) { error.textContent = result.error || "That invite could not be created."; return; }
      const invite = result.invite;
      adminInvites.unshift(invite);
      document.querySelector("#invite-list").innerHTML = adminInviteRows();
      const link = `${window.location.origin}/invite/${invite.token}`;
      const output = document.querySelector("#invite-result");
      output.innerHTML = `<strong>Invitation ready</strong><p>This link is shown once. Copy it before leaving this page.</p><div><input id="created-invite-link" readonly aria-label="New invitation link" value="${escapeHtml(link)}"><button class="secondary-button" id="copy-invite" type="button">Copy link</button></div>`;
      document.querySelector("#copy-invite")?.addEventListener("click", async () => {
        try { await navigator.clipboard.writeText(link); showError("Invitation link copied"); }
        catch { document.querySelector("#created-invite-link")?.select(); showError("Select and copy the invitation link"); }
      });
      bindAdminRowActions();
    } catch { error.textContent = "That invite could not be created. Check your connection and try again."; }
    finally { submit.disabled = false; submit.textContent = "Create invite"; }
  });
  document.querySelectorAll(".filter-feedback").forEach((button) => button.addEventListener("click", () => refreshAdminFeedback(button)));
  bindAdminRowActions();
}

function bindAdminRowActions() {
  document.querySelectorAll(".revoke-invite").forEach((button) => {
    if (button.dataset.adminBound) return;
    button.dataset.adminBound = "true";
    button.addEventListener("click", async () => {
    if (button.disabled) return;
    if (!window.confirm("Revoke this invitation? Its link will stop working immediately.")) return;
    button.disabled = true;
    try {
    const response = await fetch(`/api/admin/invites/${encodeURIComponent(button.dataset.inviteId)}/revoke`, {
      method: "POST", credentials: "include", headers: { "x-dittybag-request": "web" },
    });
    if (!response.ok) { showError("That invitation could not be revoked."); return; }
    adminInvites = adminInvites.map((invite) => invite.id === button.dataset.inviteId ? { ...invite, status: "revoked" } : invite);
    document.querySelector("#invite-list").innerHTML = adminInviteRows();
    bindAdminRowActions();
    } catch { showError("Revocation could not be confirmed. Refresh the inbox before retrying."); }
    finally { button.disabled = false; }
    });
  });
  document.querySelectorAll(".feedback-status").forEach((select) => {
    if (select.dataset.adminBound) return;
    select.dataset.adminBound = "true";
    select.addEventListener("change", async () => {
    if (select.disabled) return;
    const status = select.value, original = select.dataset.originalStatus;
    select.disabled = true;
    try {
    const response = await fetch(`/api/admin/feedback/${encodeURIComponent(select.dataset.feedbackId)}/status`, {
      method: "POST", credentials: "include",
      headers: { "content-type": "application/json", "x-dittybag-request": "web" },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) { select.value = select.dataset.originalStatus; showError("That feedback status could not be updated."); return; }
    select.dataset.originalStatus = status;
    adminFeedback = adminFeedback.map(item => item.id === select.dataset.feedbackId ? { ...item, status } : item);
    if (account?.beta) account.beta.unreadFeedback = Math.max(0, (Number(account.beta.unreadFeedback) || 0) + Number(status === "new") - Number(original === "new"));
    showError("Feedback status updated");
    } catch { select.value = original; showError("Status update could not be confirmed. Refresh the inbox before retrying."); }
    finally { select.disabled = false; }
    });
  });
}

function secondFactorView() {
  app.innerHTML = pageShell(
    `<main class="enrollment"><section><p class="eyebrow">ACCOUNT SECURITY</p><h1>Verify it’s you</h1><p>Enter the current six-digit code from your authenticator to open your DittyBag web library.</p><div class="challenge-form"><label>Authenticator code<input id="totp-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="000000"></label><button class="primary-button" id="verify-login-2fa">Open DittyBag</button><p class="form-error" id="form-error"></p><label>Lost your authenticator? Use a one-time recovery code<input id="recovery-code" autocomplete="off" placeholder="abcde-12345" maxlength="11"></label><button class="secondary-button" id="verify-recovery">Use recovery code</button></div></section></main>`,
    true,
  );
  document.querySelector("#verify-recovery")?.addEventListener("click", () => verifySecondFactor(true));
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
  document
    .querySelector("#verify-login-2fa")
    ?.addEventListener("click", verifySecondFactor);
  document.querySelector("#totp-code")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") verifySecondFactor();
  });
  document.querySelector("#totp-code")?.focus();
}

function pairingView(pairing) {
  app.innerHTML = pageShell(
    `<main class="enrollment pairing"><section><p class="eyebrow">PAIR A TRUSTED DEVICE</p><h1>${pairing.approved ? "Device approved" : "Connect this computer?"}</h1><p>${pairing.approved ? "Return to DittyBag Pro. The app will finish creating its encrypted connection." : "Only approve a device you recognize and currently control."}</p><div class="pair-device"><span class="device-glyph">${pairing.platform === "macOS" ? "⌘" : "▣"}</span><span><strong>${escapeHtml(pairing.name)}</strong><small>${escapeHtml(pairing.platform)} · DittyBag Pro ${escapeHtml(pairing.appVersion)}</small></span></div>${pairing.approved ? '<div class="security-ready"><b>✓</b><span><strong>Approved</strong><small>You may close this browser tab.</small></span></div>' : '<button class="primary-button" id="approve-pairing">Approve and connect</button><p class="form-error" id="form-error"></p>'}</section></main>`,
    true,
  );
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
  document
    .querySelector("#approve-pairing")
    ?.addEventListener("click", async () => {
      const response = await fetch(
        `/api/account/pairing/${encodeURIComponent(pairing.id)}/approve`,
        {
          method: "POST",
          credentials: "include",
          headers: { "x-dittybag-request": "web" },
        },
      );
      if (!response.ok)
        return showFormError(
          (await response.json().catch(() => ({}))).error ||
            "This device could not be approved.",
        );
      pairingView({ ...pairing, approved: true });
    });
}

async function enrollmentView(data) {
  const qr = await QRCode.toDataURL(data.totpURI, {
    width: 220,
    margin: 1,
    color: { dark: "#17130d", light: "#ffffff" },
  });
  app.innerHTML = pageShell(
    `<main class="enrollment"><section><p class="eyebrow">ACCOUNT SECURITY</p><h1>Set up your authenticator</h1><p>Scan this code, enter the current six-digit code, then save the recovery codes somewhere secure.</p><div class="enrollment-grid"><div class="qr-card"><img src="${qr}" alt="Authenticator QR code"><small>Issuer: DittyBag Pro</small></div><div><label>Verification code<input id="totp-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="000000"></label><button class="primary-button" id="verify-2fa">Verify and enable</button><p class="form-error" id="form-error"></p></div></div><div class="backup-codes"><h2>Recovery codes</h2><p>Each code works once. Download or copy them before continuing.</p><div>${data.backupCodes.map((code) => `<code>${escapeHtml(code)}</code>`).join("")}</div><button class="secondary-button" id="copy-recovery">Copy recovery codes</button></div></section></main>`,
    true,
  );
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
  document
    .querySelector("#copy-recovery")
    ?.addEventListener("click", async () =>
      navigator.clipboard.writeText(data.backupCodes.join("\n")),
    );
  document
    .querySelector("#verify-2fa")
    ?.addEventListener("click", verifyEnrollment);
  document.querySelector("#totp-code")?.focus();
}

async function signIn() {
  const pair = new URLSearchParams(location.search).get("pair");
  const callback = pair
    ? `${window.location.origin}/?pair=${encodeURIComponent(pair)}`
    : `${window.location.origin}/app`;
  const result = await authClient.signIn.social({
    provider: "google",
    callbackURL: callback,
    errorCallbackURL: pair
      ? `${window.location.origin}/?pair=${encodeURIComponent(pair)}&auth_error=google`
      : `${window.location.origin}/?auth_error=google`,
  });
  if (result.error)
    showError(result.error.message || "Google sign-in could not start.");
}

async function fetchEncryptedHistory(signal) {
  const events = [];
  let cursor = 0;
  for (let page = 0; page < 40; page += 1) {
    const response = await fetch(`/api/account/history?cursor=${cursor}&limit=50`, { credentials: "include", signal });
    if (!response.ok) throw new Error(await historyDownloadError(response));
    const result = await response.json();
    if (!Array.isArray(result.events) || events.length + result.events.length > 500) throw new Error("Web History reached its item safety limit.");
    events.push(...result.events);
    cursor = result.nextCursor;
    if (!result.hasMore) break;
  }
  return events;
}

async function historyDownloadError(response) {
  const result = await response.clone().json().catch(() => ({}));
  if (result.twoFactorRequired) return "Your secure web session expired. Refresh the page and verify your authenticator again.";
  return result.error || "Encrypted History could not be downloaded.";
}

async function fetchEncryptedEvent(event, signal) {
  const chunks = [];
  let cursor = 0;
  while (cursor < Number(event.chunkCount)) {
    const response = await fetch(`/api/account/history/${encodeURIComponent(event.id)}/chunks?cursor=${cursor}&limit=2`, { credentials: "include", signal });
    if (!response.ok) throw new Error(await historyDownloadError(response));
    const page = await response.json();
    if (!Array.isArray(page.chunks) || !page.chunks.length) throw new Error("An encrypted History item is incomplete in cloud storage.");
    chunks.push(...page.chunks);
    cursor = Number(page.nextChunk);
  }
  return { ...event, chunks };
}

function updateHistoryProgress(completed, total) {
  const button = document.querySelector("#unlock-history");
  if (button) button.textContent = total ? `Decrypting ${completed} of ${total}…` : "Preparing encrypted History…";
}

function stopHistoryCountdown() {
  if (historyCountdownTimer) clearInterval(historyCountdownTimer);
  historyCountdownTimer = null;
}

function startHistoryCountdown() {
  stopHistoryCountdown();
  const update = () => {
    const remaining = Number(webHistory.expiresAt) - Date.now();
    if (remaining <= 0) {
      lockWebHistory("Web History locked because its timer expired.");
      return;
    }
    const target = document.querySelector("#history-lock-countdown");
    if (target) target.textContent = formatHistoryUnlockRemaining(webHistory.expiresAt);
  };
  update();
  historyCountdownTimer = setInterval(update, 1000);
}

async function unlockWebHistoryWithKey(recoveryKey, { expiresAt = 0, durationMinutes = defaultHistoryUnlockMinutes } = {}) {
  let key = null;
  let accumulator = null;
  const generation = sessionGeneration;
  const userId = account?.user?.id;
  historyAbortController?.abort("replaced");
  historyAbortController = new AbortController();
  const signal = historyAbortController.signal;
  webHistory = { ...webHistory, loading: true, error: "" };
  dashboard();
  try {
    key = parseRecoveryKey(recoveryKey);
    const encrypted = await fetchEncryptedHistory(signal);
    webHistory.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    accumulator = createWebHistoryAccumulator();
    updateHistoryProgress(0, encrypted.length);
    for (let index = 0; index < encrypted.length; index += 1) {
      const event = await fetchEncryptedEvent(encrypted[index], signal);
      applySyncEvent(accumulator, event, key);
      updateHistoryProgress(index + 1, encrypted.length);
    }
    if (signal.aborted || historyAbortController?.signal !== signal || generation !== sessionGeneration) throw new DOMException("History unlock cancelled", "AbortError");
    const materialized = finishWebHistory(accumulator);
    let activeUntil = Number(expiresAt);
    if (!activeUntil) {
      activeUntil = Date.now() + Number(durationMinutes) * 60_000;
      try { activeUntil = saveHistoryUnlockSession(sessionStorage, userId, recoveryKey, Number(durationMinutes)); }
      catch { showError("History is unlocked, but this browser cannot preserve it through a refresh."); }
    }
    webHistory = { unlocked: true, loading: false, error: "", expiresAt: activeUntil, ...materialized };
    dashboard();
    startHistoryCountdown();
  } catch (error) {
    disposeWebHistoryAccumulator(accumulator);
    if (historyAbortController?.signal !== signal || generation !== sessionGeneration) return;
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
    webHistory = { unlocked: false, loading: false, clips: [], objectUrls: [], expiresAt: 0,
      error: signal.aborted ? "" : error.message || "History could not be decrypted." };
    dashboard();
  } finally {
    key?.fill(0);
    if (historyAbortController?.signal === signal) historyAbortController = null;
  }
}

async function unlockWebHistory() {
  const recoveryKey = document.querySelector("#history-recovery-key")?.value ?? "";
  const durationMinutes = Number(document.querySelector("#history-unlock-duration")?.value) || defaultHistoryUnlockMinutes;
  await unlockWebHistoryWithKey(recoveryKey, { durationMinutes });
}

function clearLocalHistory(reason) {
  stopHistoryCountdown();
  historyAbortController?.abort(reason);
  historyAbortController = null;
  try { clearHistoryUnlockSession(sessionStorage); } catch {}
  webHistory.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  webHistory = { unlocked: false, loading: false, clips: [], objectUrls: [], error: "", expiresAt: 0 };
}

function lockWebHistory(message = "") {
  clearLocalHistory("locked");
  dashboard();
  if (message) showError(message);
}

async function copyWebClip(clip) {
  const imageUrl = safeWebImageUrl(clip.webImageUrl);
  if (clip.kind === "image" && imageUrl && window.ClipboardItem) {
    const blob = await fetch(imageUrl).then((response) => response.blob());
    await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
  } else {
    await navigator.clipboard.writeText(String(clip.text ?? ""));
  }
  showError("Copied");
}

function showImagePreview(clip) {
  const imageUrl = safeWebImageUrl(clip.webImageUrl);
  if (!imageUrl) return;
  const copiedAt = clipCopiedAt(clip);
  const returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const background = document.querySelector(".site-shell");
  const dialog = document.createElement("div");
  dialog.className = "web-image-lightbox";
  dialog.innerHTML = `<section role="dialog" aria-modal="true" aria-label="Expanded screenshot"><header><div><strong>${escapeHtml(clip.text || "Screenshot")}</strong><small>${escapeHtml(clipDeviceNames(clip))}${copiedAt ? ` · ${escapeHtml(new Date(copiedAt).toLocaleString())}` : ""}</small></div><button class="close-image-preview" aria-label="Close image preview">×</button></header><div class="web-image-stage"><img src="${escapeHtml(imageUrl)}" alt="Expanded synced screenshot"></div><footer><span>${clip.width && clip.height ? `${Number(clip.width)} × ${Number(clip.height)}` : "Full decrypted image"}</span><a href="${escapeHtml(imageUrl)}" download="dittybag-image">Save image</a></footer></section>`;
  const close = () => {
    document.removeEventListener("keydown", onKeyDown);
    background?.removeAttribute("inert");
    background?.removeAttribute("aria-hidden");
    dialog.remove();
    returnFocus?.focus();
  };
  const onKeyDown = (event) => {
    if (event.key === "Escape") { event.preventDefault(); close(); return; }
    if (event.key !== "Tab") return;
    const focusable = [...dialog.querySelectorAll("button, a[href]")].filter((item) => !item.hasAttribute("disabled"));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  dialog.addEventListener("click", (event) => { if (event.target === dialog) close(); });
  dialog.querySelector(".close-image-preview")?.addEventListener("click", close);
  document.addEventListener("keydown", onKeyDown);
  background?.setAttribute("inert", "");
  background?.setAttribute("aria-hidden", "true");
  document.body.append(dialog);
  dialog.querySelector(".close-image-preview")?.focus();
}

function bindHistoryActions() {
  document.querySelector("#unlock-history")?.addEventListener("click", unlockWebHistory);
  document.querySelector("#history-recovery-key")?.addEventListener("keydown", (event) => {
    if (event.key === "Enter") unlockWebHistory();
  });
  document.querySelector("#lock-history")?.addEventListener("click", () => lockWebHistory());
  const copyIndex = async (index) => {
    const clip = webHistory.clips[Number(index)];
    if (!clip) return;
    try { await copyWebClip(clip); } catch { showError("This browser could not copy that item."); }
  };
  document.querySelectorAll(".copy-web-clip").forEach((button) => button.addEventListener("click", async (event) => {
    event.stopPropagation();
    await copyIndex(button.dataset.clipIndex);
  }));
  document.querySelectorAll(".web-clip-image").forEach((button) => button.addEventListener("click", (event) => {
    event.stopPropagation();
    const clip = webHistory.clips[Number(button.dataset.clipIndex)];
    if (clip) showImagePreview(clip);
  }));
  document.querySelectorAll(".save-web-clip").forEach((link) => link.addEventListener("click", (event) => event.stopPropagation()));
  document.querySelectorAll(".web-clip").forEach((row) => row.addEventListener("click", (event) => {
    if (event.target instanceof Element && event.target.closest("button, a")) return;
    copyIndex(row.dataset.clipIndex);
  }));
}

function bindFeedbackActions() {
  let sending = false;
  const form = document.querySelector("#feedback-form");
  const message = document.querySelector("#feedback-message");
  const count = document.querySelector("#feedback-count");
  const updateCount = () => { if (count && message) count.textContent = `${message.value.length} / 4000`; };
  message?.addEventListener("input", updateCount);
  form?.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (sending) return;
    const error = document.querySelector("#feedback-error");
    const success = document.querySelector("#feedback-success");
    const submit = form.querySelector('button[type="submit"]');
    const category = document.querySelector("#feedback-category");
    const submittedText = message?.value ?? "";
    const body = {
      category: category?.value ?? "experience",
      message: submittedText.trim(),
    };
    if (!body.message) {
      if (error) error.textContent = "Tell us what you’d like us to know.";
      message?.setAttribute("aria-invalid", "true");
      message?.focus();
      return;
    }
    if (error) error.textContent = "";
    if (success) success.textContent = "";
    message?.removeAttribute("aria-invalid");
    sending = true;
    submit.disabled = true;
    submit.setAttribute("aria-busy", "true");
    submit.textContent = "Sending…";
    try {
      const response = await fetch("/api/account/feedback", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", "x-dittybag-request": "web" },
        body: JSON.stringify(body),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (error) error.textContent = response.status === 429
          ? "You’ve sent several notes recently. Please try again in about an hour."
          : result.error || "Your feedback could not be sent. Please try again.";
        return;
      }
      const draftUnchanged = message.value === submittedText && category?.value === body.category;
      if (draftUnchanged) message.value = "";
      updateCount();
      if (success) success.textContent = draftUnchanged
        ? "Thanks — your feedback was sent privately to the DittyBag team."
        : "Your earlier feedback was sent. Your newer edits are still here and have not been sent.";
    } catch {
      if (error) error.textContent = "Your feedback could not be sent. Check your connection and try again.";
    } finally {
      sending = false;
      submit.disabled = false;
      submit.removeAttribute("aria-busy");
      submit.textContent = "Send feedback";
    }
  });
}

// Lock this tab before any network call so an offline or failing server
// cannot leave decrypted History or the saved recovery key behind.
async function signOut() {
  sessionGeneration += 1;
  account = null;
  clearLocalHistory("signed-out");
  history.replaceState({}, "", "/");
  landing();
  let ended = true;
  try {
    try {
      const cleared = await fetch("/api/account/clear-second-factor", {
        method: "POST",
        credentials: "include",
        headers: { "x-clipstack-request": "web" },
      });
      if (!cleared.ok) ended = false;
    } catch { ended = false; }
    try {
      const result = await authClient.signOut();
      if (result?.error) ended = false;
    } catch { ended = false; }
  } finally {
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
  }
  if (!ended) signOutFailedNotice();
}

function signOutFailedNotice() {
  const main = document.querySelector("main.landing");
  if (!main) return;
  main.querySelector("#sign-out-failed")?.remove();
  main.insertAdjacentHTML("afterbegin", '<div class="auth-error" id="sign-out-failed" role="alert"><strong>Signed out on this tab, but the server didn’t confirm.</strong><span>History is locked here. Your session may still be active until it expires. Check your connection and try again.</span><button class="secondary-button" id="retry-sign-out">Try sign-out again</button></div>');
  main.querySelector("#retry-sign-out")?.addEventListener("click", signOut);
}

function bindAccountActions() {
  document.querySelector("#sign-out")?.addEventListener("click", signOut);
  document.querySelector("#enable-2fa")?.addEventListener("click", async () => {
    const result = await authClient.twoFactor.enable({
      method: "totp",
      issuer: "DittyBag Pro",
    });
    if (result.error)
      return showError(result.error.message || "2FA setup could not start.");
    enrollment = result.data;
    await enrollmentView(enrollment);
  });
  document
    .querySelector("#disable-2fa")
    ?.addEventListener("click", async () => {
      if (
        !window.confirm(
          "Disable authenticator protection for this DittyBag account?",
        )
      )
        return;
      const code = window.prompt(
        "Enter the current six-digit code from your authenticator to disable 2FA.",
      )?.trim();
      if (!code) return;
      if (!/^\d{6}$/.test(code))
        return showError("Enter a valid six-digit authenticator code.");
      const verification = await fetch("/api/account/verify-second-factor", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
          "x-dittybag-request": "web",
        },
        body: JSON.stringify({ code }),
      });
      if (!verification.ok)
        return showError("That authenticator code was not accepted.");
      const result = await authClient.twoFactor.disable({});
      if (result.error)
        return showError(result.error.message || "2FA could not be disabled.");
      await loadAccount();
    });
  document.querySelectorAll(".revoke-device").forEach((button) =>
    button.addEventListener("click", async () => {
      const device = devices.find(
        (entry) => entry.id === button.dataset.deviceId,
      );
      if (
        !device ||
        !window.confirm(
          `Revoke ${device.name}? It will immediately lose sync access.`,
        )
      )
        return;
      const response = await fetch(
        `/api/account/devices/${encodeURIComponent(device.id)}/revoke`,
        {
          method: "POST",
          credentials: "include",
          headers: { "x-dittybag-request": "web" },
        },
      );
      if (!response.ok) return showError("That device could not be revoked.");
      await loadAccount();
    }),
  );
}

async function verifyEnrollment() {
  const code = document.querySelector("#totp-code")?.value.trim();
  if (!/^\d{6}$/.test(code))
    return showFormError("Enter the six-digit code from your authenticator.");
  // Better Auth enables the factor and rotates the session cookie here; the
  // account challenge route would delete the session without replacing it.
  const result = await authClient.twoFactor.verifyTotp({ code });
  if (result.error)
    return showFormError(result.error.message || "That code was not accepted.");
  enrollment = null;
  history.replaceState({}, "", "/app");
  await loadAccount();
}

async function verifySecondFactor(useRecovery = false) {
  useRecovery = useRecovery === true;
  const code = document.querySelector(useRecovery ? "#recovery-code" : "#totp-code")?.value.trim();
  if (!(useRecovery ? /^[A-Za-z0-9]{5}-[A-Za-z0-9]{5}$/ : /^\d{6}$/).test(code ?? ""))
    return showFormError(useRecovery ? "Enter one recovery code, including the hyphen." : "Enter the six-digit code from your authenticator.");
  const response = await fetch("/api/account/verify-second-factor", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-dittybag-request": "web",
    },
    body: JSON.stringify(useRecovery ? { recoveryCode: code } : { code }),
  });
  if (!response.ok)
    return showFormError(
      (await response.json().catch(() => ({}))).error ||
        "That code was not accepted.",
    );
  await loadAccount();
}

function showFormError(message) {
  const target = document.querySelector("#form-error");
  if (target) target.textContent = message;
}

function showError(message) {
  const notice = document.createElement("div");
  notice.className = "toast";
  notice.setAttribute("role", "status");
  notice.setAttribute("aria-live", "polite");
  notice.textContent = message;
  document.body.append(notice);
  setTimeout(() => notice.remove(), 3500);
}

async function loadAccount() {
  const generation = sessionGeneration;
  const response = await fetch("/api/account", { credentials: "include" });
  if (generation !== sessionGeneration) return;
  if (response.status === 401) {
    stopHistoryCountdown();
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
    history.replaceState({}, "", "/");
    landing();
    return;
  }
  if (response.status === 403) {
    secondFactorView();
    return;
  }
  if (!response.ok)
    return showError("DittyBag account details could not be loaded.");
  const loaded = await response.json();
  if (generation !== sessionGeneration) return;
  account = loaded;
  if (location.pathname === "/admin") {
    if (!account.beta?.isAdmin) {
      history.replaceState({}, "", "/app");
      dashboard();
      showError("That page is available only to beta administrators.");
      return;
    }
    await loadAdminDashboard();
    return;
  }
  const deviceResponse = await fetch("/api/account/devices", {
    credentials: "include",
  });
  const loadedDevices = deviceResponse.ok ? (await deviceResponse.json()).devices : [];
  if (generation !== sessionGeneration) return;
  devices = loadedDevices;
  const pair = new URLSearchParams(location.search).get("pair");
  if (pair) {
    const pairingResponse = await fetch(
      `/api/account/pairing/${encodeURIComponent(pair)}`,
      { credentials: "include" },
    );
    const pairing = await pairingResponse.json().catch(() => ({}));
    if (generation !== sessionGeneration) return;
    if (!pairingResponse.ok)
      return showError(pairing.error || "That pairing request is unavailable.");
    pairingView(pairing);
    return;
  }
  let savedUnlock = null;
  try { savedUnlock = readHistoryUnlockSession(sessionStorage, account.user?.id); } catch {}
  if (savedUnlock) {
    await unlockWebHistoryWithKey(savedUnlock.recoveryKey, { expiresAt: savedUnlock.expiresAt });
  } else {
    dashboard();
  }
}

async function start() {
  const params = new URLSearchParams(location.search);
  const errorCode = params.get("error");
  const authFailed =
    params.has("auth_error") ||
    location.pathname === "/error" ||
    Boolean(errorCode);
  let authError = "";
  if (authFailed) {
    authError = authErrorMessage(errorCode);
    const pair = params.get("pair");
    history.replaceState({}, "", pair ? `/?pair=${encodeURIComponent(pair)}` : "/");
  }
  const session = await authClient.getSession();
  // Only the signed-in app path may restore a saved History unlock.
  if (location.pathname === "/invite" || location.pathname.startsWith("/invite/")) {
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
    await inviteLanding(Boolean(session.data));
    return;
  }
  const publicPage = publicPages[location.pathname];
  if (publicPage) {
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
    publicPage(Boolean(session.data));
    return;
  }
  if (session.data) {
    if (location.pathname === "/" && !params.has("pair"))
      history.replaceState({}, "", "/app");
    await loadAccount();
  } else {
    try { clearHistoryUnlockSession(sessionStorage); } catch {}
    landing(authError);
  }
}

start();
